<template>
  <header class="common-header">
    <div class="header-content">
      <!-- 移动端左侧汉堡菜单按钮 -->
      <button
        type="button"
        class="mobile-nav-toggle"
        :aria-label="$t('header.openNav')"
        @click="openMobileNav"
      >
        <span class="hamburger-line" />
        <span class="hamburger-line" />
        <span class="hamburger-line" />
      </button>

      <div
        class="logo-section"
        role="button"
        tabindex="0"
        @click="goHome"
        @keydown.enter.prevent="goHome"
        @keydown.space.prevent="goHome"
      >
        <img
          src="@/assets/icons/logo.png"
          :alt="$t('header.logoAlt')"
          class="logo-img"
        >
      </div>
      
      <div class="header-actions">
        <!-- 桌面端顶部导航按钮 -->
        <div class="header-nav-buttons">
          <el-button
            v-if="$auth.canAccessWorkbench()"
            class="header-btn workbench-btn"
            @click="goToWorkbench"
          >
            {{ $t('header.workbench') }}
          </el-button>
          <el-button
            class="header-btn primary-btn"
            @click="handleFreeTrialClick"
          >
            {{ $t('header.freeTrial') }}
          </el-button>
          <el-button
            class="header-btn primary-btn primary-btn-solid"
            @click="goToIPManagement"
          >
            {{ $t('header.ipManagement') }}
          </el-button>
          <el-button
            class="header-btn primary-btn certificate-report-btn"
            @click="goToCertificateReportVerification"
          >
            {{ $t('header.certificateReportVerification') }}
          </el-button>
        </div>

        <LanguageSwitcher />
        <PlatformGuide
          ref="platformGuide"
          @menu-toggle="handleMenuToggle"
        />
        <MessageList
          ref="messageList"
          :user-logged-in="userLoggedIn"
          @menu-toggle="handleMenuToggle"
        />
        <UserMenu
          ref="userMenu"
          @open-login="showLoginDialog = true"
          @menu-toggle="handleMenuToggle"
        />
      </div>
    </div>

    <!-- 移动端左侧抽屉导航 -->
    <el-drawer
      v-model="mobileNavVisible"
      direction="ltr"
      :size="mobileDrawerSize"
      append-to-body
      :show-close="false"
      class="mobile-nav-drawer"
      @closed="handleMobileNavClosed"
    >
      <template #header>
        <div class="mobile-nav-drawer-header">
          <span class="mobile-nav-drawer-title">{{ $t('header.navMenu') }}</span>
          <button
            type="button"
            class="mobile-nav-close"
            :aria-label="$t('header.closeNav')"
            @click="closeMobileNav"
          >
            <el-icon><Close /></el-icon>
          </button>
        </div>
      </template>

      <nav class="mobile-nav-list">
        <button
          v-if="$auth.canAccessWorkbench()"
          type="button"
          class="mobile-nav-item mobile-nav-item--outline"
          @click="runMobileNavAction(goToWorkbench)"
        >
          {{ $t('header.workbench') }}
        </button>
        <button
          type="button"
          class="mobile-nav-item mobile-nav-item--primary"
          @click="runMobileNavAction(handleFreeTrialClick)"
        >
          {{ $t('header.freeTrial') }}
        </button>
        <button
          type="button"
          class="mobile-nav-item mobile-nav-item--primary-solid"
          @click="runMobileNavAction(goToIPManagement)"
        >
          {{ $t('header.ipManagement') }}
        </button>
        <button
          type="button"
          class="mobile-nav-item mobile-nav-item--certificate"
          @click="runMobileNavAction(goToCertificateReportVerification)"
        >
          {{ $t('header.certificateReportVerification') }}
        </button>
      </nav>
    </el-drawer>

    <LoginDialog v-model:visible="showLoginDialog" />
    <LoginAuthDialog
      v-model="showLoginAuthDialog"
      scene="ipManagement"
      @confirm="showLoginAuthDialog = false"
      @cancel="showLoginAuthDialog = false"
    />
  </header>
</template>

<script>
import { Close } from '@element-plus/icons-vue'
import LoginDialog from '@/views/login/index.vue'
import LoginAuthDialog from '@/components/LoginAuthDialog/index.vue'
import LanguageSwitcher from './LanguageSwitcher.vue'
import MessageList from './MessageList.vue'
import PlatformGuide from './PlatformGuide.vue'
import UserMenu from './UserMenu.vue'

export default {
  name: 'CommonHeader',
  components: {
    Close,
    LoginDialog,
    LoginAuthDialog,
    LanguageSwitcher,
    MessageList,
    PlatformGuide,
    UserMenu
  },
  data() {
    return {
      showLoginDialog: false, // 登录弹窗显示状态
      showLoginAuthDialog: false, // 知识产权管理认证弹窗显示状态
      mobileNavVisible: false // 移动端左侧导航抽屉显示状态
    }
  },
  computed: {
    // 检查用户是否已登录
    userLoggedIn() {
      return this.$auth.isLoggedIn()
    },
    // 移动端抽屉宽度：小屏占满大部分宽度，大屏固定 300px
    mobileDrawerSize() {
      if (typeof window === 'undefined') {
        return '300px'
      }
      return window.innerWidth <= 480 ? '82vw' : '300px'
    }
  },
  async mounted() {
    // 监听全局点击事件，点击外部区域关闭菜单
    document.addEventListener('click', this.handleClickOutside)
    // 监听显示登录弹窗的全局事件
    window.addEventListener('show-login-dialog', this.handleShowLoginDialog)
  },
  beforeUnmount() {
    // 移除全局点击事件监听
    document.removeEventListener('click', this.handleClickOutside)
    // 移除显示登录弹窗的事件监听
    window.removeEventListener('show-login-dialog', this.handleShowLoginDialog)
  },
  methods: {
    /** 打开移动端左侧导航抽屉 */
    openMobileNav() {
      this.closeAllHeaderMenus()
      this.mobileNavVisible = true
    },
    /** 关闭移动端左侧导航抽屉 */
    closeMobileNav() {
      this.mobileNavVisible = false
    },
    /** 抽屉关闭后的回调 */
    handleMobileNavClosed() {
      this.mobileNavVisible = false
    },
    /** 执行抽屉菜单项并自动关闭抽屉 */
    runMobileNavAction(action) {
      this.closeMobileNav()
      if (typeof action === 'function') {
        action.call(this)
      }
    },
    /** 关闭头部所有下拉菜单 */
    closeAllHeaderMenus() {
      if (this.$refs.platformGuide) {
        this.$refs.platformGuide.closeGuideMenu()
      }
      if (this.$refs.messageList) {
        this.$refs.messageList.closeMessageMenu()
      }
      if (this.$refs.userMenu) {
        this.$refs.userMenu.closeUserMenu()
      }
    },
    handleFreeTrialClick() {
      this.$router.push('/user-center/rights/free-rights')
    },
    /**
     * 处理菜单切换，关闭其他菜单
     */
    handleMenuToggle(menuType) {
      if (menuType === 'message') {
        if (this.$refs.platformGuide) {
          this.$refs.platformGuide.closeGuideMenu()
        }
        if (this.$refs.userMenu) {
          this.$refs.userMenu.closeUserMenu()
        }
      } else if (menuType === 'guide') {
        if (this.$refs.messageList) {
          this.$refs.messageList.closeMessageMenu()
        }
        if (this.$refs.userMenu) {
          this.$refs.userMenu.closeUserMenu()
        }
      } else if (menuType === 'user') {
        if (this.$refs.platformGuide) {
          this.$refs.platformGuide.closeGuideMenu()
        }
        if (this.$refs.messageList) {
          this.$refs.messageList.closeMessageMenu()
        }
      }
    },
    /**
     * 处理显示登录弹窗的全局事件
     */
    handleShowLoginDialog() {
      this.showLoginDialog = true
    },
    /**
     * 处理点击外部区域
     * @param {Event} event - 点击事件
     */
    handleClickOutside(event) {
      const userDropdown = this.$el?.querySelector('.user-dropdown')
      const guideIconWrapper = this.$el?.querySelector('.guide-icon-wrapper')
      const messageIconWrapper = this.$el?.querySelector('.message-icon-wrapper')
      
      if (userDropdown && !userDropdown.contains(event.target)) {
        if (this.$refs.userMenu) {
          this.$refs.userMenu.closeUserMenu()
        }
      }
      if (guideIconWrapper && !guideIconWrapper.contains(event.target)) {
        if (this.$refs.platformGuide) {
          this.$refs.platformGuide.closeGuideMenu()
        }
      }
      if (messageIconWrapper && !messageIconWrapper.contains(event.target)) {
        if (this.$refs.messageList) {
          this.$refs.messageList.closeMessageMenu()
        }
      }
    },
    /** 回首页并整页刷新：与其它页面区分，重新拉取首页与全局状态 */
    goHome() {
      // 生产环境 BASE_URL 为 PUBLIC_PATH（如 /copyright-association/portal/），勿写死 '/'
      window.location.href = process.env.BASE_URL || '/'
    },
    /**
     * 跳转到工作台
     */
    goToWorkbench() {
      this.$router.push('/workbench')
    },
    /**
     * 跳转知识产权管理（与介绍页一致：校验登录/认证后新窗口打开）
     */
    goToIPManagement() {
      if (!this.isIpManagementAuthPassed()) {
        this.showLoginAuthDialog = true
        return
      }
      const routeData = this.$router.resolve({ name: 'IntellectualPropertyManagement' })
      window.open(routeData.href, '_blank')
    },
    isIpManagementAuthPassed() {
      return this.$auth?.isLoggedIn?.() && this.$auth?.isIdentityVerified?.()
    },
    /**
     * 跳转证书报告验证页
     */
    goToCertificateReportVerification() {
      this.$router.push({ name: 'CertificateReportVerification' })
    }
  }
}
</script>

<style lang="scss" scoped>
.common-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: #ffffff;
  color: #333333;
  padding: 8px 0;
  border-bottom: 1px solid #e4e7ed;

  .header-content {
    max-width: 1400px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 30px;
    gap: 12px;
  }

  // 移动端汉堡菜单按钮（桌面端隐藏）
  .mobile-nav-toggle {
    display: none;
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    padding: 0;
    margin: 0;
    border: none;
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 5px;
    transition: background 0.2s ease;

    &:hover {
      background: #f5f7fa;
    }

    &:active {
      background: #eef1f5;
    }

    .hamburger-line {
      display: block;
      width: 20px;
      height: 2px;
      border-radius: 1px;
      background: #1a1a1a;
    }
  }

  .logo-section {
    display: flex;
    align-items: center;
    cursor: pointer;
    transition: opacity 0.15s ease, transform 0.12s ease;
    user-select: none;

    &:active {
      opacity: 0.82;
      transform: scale(0.985);
    }

    .logo-img {
      height: 68px;
      width: auto;
      display: block;
      object-fit: contain;
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;

    .header-nav-buttons {
      display: flex;
      align-items: center;
      gap: 2px;
    }

    .header-btn {
      height: 39px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      font-size: 14px;
      border-radius: 22px;
      transition: background 0.2s ease, border-color 0.2s ease, opacity 0.15s ease, transform 0.12s ease, box-shadow 0.12s ease;
      font-weight: bold;
      cursor: pointer;
      user-select: none;

      &:active {
        opacity: 0.82;
        transform: scale(0.985);
        box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      &.primary-btn {
        font-family: PingFang SC, PingFang SC;
        font-weight: 500;
        font-size: 15px;
        color: #ffffff;
        text-align: center;
        font-style: normal;
        text-transform: none;
        background: linear-gradient(90deg, #0d62f3 0%, #1b39df 100%);
        border-radius: 22px;
        border: none;
        padding: 0 24px;

        &:hover {
          background: linear-gradient(90deg, #2568f5 0%, #2a45e8 100%);
        }

        &:active {
          background: linear-gradient(90deg, #0b56e0 0%, #1735d6 100%);
        }

        &.primary-btn-solid {
          background: #0d62f3;
          border-radius: 22px;
          &:hover {
            background: #2568f5;
          }

          &:active {
            background: #0b56e0;
          }
        }
      }

      &.workbench-btn {
        background: #ffffff;
        color: #666666;
        border: 1px solid #e0e0e0;
        padding: 0 24px;
        
        &:hover {
          background: #f5f7fa;
          border-color: #d0d0d0;
        }

        &:active {
          background: #eef1f5;
          border-color: #c5c5c5;
        }
      }

      &.icon-btn {
        color: #000000;
        font-size: 20px;
        padding: 0px;
        
        &:hover {
          color: #0d63f3;
          background-color: transparent;
        }
      }
      

      &.certificate-report-btn {
          background: linear-gradient(90deg, #962DFF 0%, #5A1B99 100%);

          &:hover {
            background: linear-gradient(90deg, #a54bff 0%, #6a28ad 100%);
          }

          &:active {
            background: linear-gradient(90deg, #872ee6 0%, #521a8a 100%);
          }
        }
    }
  }
}

// 响应式设计
// 平板适配 (768px - 1024px)
@media (max-width: 1024px) and (min-width: 769px) {
  .common-header {
    .header-content {
      padding: 0 20px;
    }

    .header-actions {
      gap: 6px;

      .header-btn {
        &.primary-btn {
          font-size: 13px;
          padding: 8px 16px;
          border-radius: 22px;
        }

        &.certificate-report-btn {
          background: linear-gradient(90deg, #962DFF 0%, #5A1B99 100%);

          &:hover {
            background: linear-gradient(90deg, #a54bff 0%, #6a28ad 100%);
          }
        }
      }
    }
  }
}

// 移动端适配 (≤ 768px)
@media (max-width: 768px) {
  .common-header {
    padding: 12px 0;

    .header-content {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      padding: 0 15px;
      gap: 10px;
    }

    .logo-section {
      flex: 1;
      min-width: 0;
      text-align: left;
      display: flex;
      align-items: center;

      .logo-img {
        height: 40px;
        width: auto;
      }
    }

    .mobile-nav-toggle {
      display: inline-flex;
    }

    .header-actions {
      flex-wrap: nowrap;
      justify-content: flex-end;
      gap: 6px;
      flex-shrink: 0;

      .header-nav-buttons {
        display: none;
      }
    }
  }
}

// 移动端左侧抽屉样式
.mobile-nav-drawer {
  :deep(.el-drawer__header) {
    margin-bottom: 0;
    padding: 16px 16px 12px;
    border-bottom: 1px solid #eef0f3;
  }

  :deep(.el-drawer__body) {
    padding: 16px;
  }

  .mobile-nav-drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .mobile-nav-drawer-title {
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
  }

  .mobile-nav-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #606266;
    cursor: pointer;

    &:hover {
      background: #f5f7fa;
    }
  }

  .mobile-nav-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .mobile-nav-item {
    width: 100%;
    min-height: 48px;
    padding: 0 20px;
    border: none;
    border-radius: 24px;
    font-size: 15px;
    font-weight: 500;
    color: #ffffff;
    cursor: pointer;
    text-align: center;
    transition: opacity 0.15s ease, transform 0.12s ease;

    &:active {
      opacity: 0.85;
      transform: scale(0.985);
    }

    &--outline {
      background: #ffffff;
      color: #666666;
      border: 1px solid #e0e0e0;
    }

    &--primary {
      background: linear-gradient(90deg, #0d62f3 0%, #1b39df 100%);
    }

    &--primary-solid {
      background: #0d62f3;
    }

    &--certificate {
      background: linear-gradient(90deg, #962dff 0%, #5a1b99 100%);
    }
  }
}

// 小屏幕手机适配 (≤ 480px)
@media (max-width: 480px) {
  .common-header {
    padding: 10px 0;

    .header-content {
      padding: 0 12px;
    }

    .logo-section {
      .logo-img {
        height: 32px;
      }
    }

    .header-actions {
      gap: 4px;
    }
  }
}

// 呼吸动画
@keyframes breathe {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.2);
  }
}
</style>
